import React, { useEffect, useMemo, useState } from 'react';
import TrackerModal from './TrackerModal';
import TrackerIcon from './TrackerIcon';
import { TrackerCatalyst, TrackerPermissions } from '../../types';
import { isMine, isReadoutRow, isReadoutType } from './readThrough';
import { fmtLong } from './helpers';
import { catalystTrackerApi } from '../../services/catalystTrackerApi';
import { cleanTerm, deepLeft, deepNext, hhmm, preqSlug } from './searchFlow';

interface Props {
  rows: TrackerCatalyst[];
  company: string;
  permissions: TrackerPermissions | null;
  searchLog: string[] | undefined;
  onRecordSearch: () => void;
  onClose: () => void;
}

type Relationship = 'competitor' | 'partner' | 'combo' | 'other';

const REL_OPTIONS: { value: Relationship; label: (c: string) => string }[] = [
  { value: 'competitor', label: (c) => `It competes with one of ${c}'s drugs` },
  { value: 'partner', label: (c) => `${c} licenses, co-develops or co-commercializes this drug` },
  { value: 'combo', label: (c) => `It is combined with one of ${c}'s drugs` },
  { value: 'other', label: () => 'Other' },
];

const WEEK_MS = 7 * 864e5;

/** openPerspFind()/openPerspReq() (tracker.js:783-821): request a verified link between a specific
 * tracked catalyst and the Perspective company, or run a weekly general scan across every catalyst.
 * Both queue a real worker search via catalystTrackerApi.fireTrigger() — see searchFlow.ts's header. */
const PerspectiveRequestModal: React.FC<Props> = ({ rows, company, permissions, searchLog, onRecordSearch, onClose }) => {
  const [query, setQuery] = useState('');
  const [target, setTarget] = useState<TrackerCatalyst | null>(null);
  const [relationship, setRelationship] = useState<Relationship>('competitor');
  const [note, setNote] = useState('');
  const [source, setSource] = useState('');
  const [msg, setMsg] = useState('');
  const [sent, setSent] = useState(false);

  const [lastScan, setLastScan] = useState<{ requestedAt: string; keyword?: string; status?: string; added?: number; updated?: number } | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    catalystTrackerApi.listSearches().then((docs) => {
      if (cancelled) return;
      let best: { requestedAt: string; keyword?: string; status?: string; added?: number; updated?: number } | null = null;
      for (const d of docs) {
        if (!d.id.startsWith('general-scan-') || !d.data.requestedAt) continue;
        if (!best || d.data.requestedAt > best.requestedAt) {
          best = { requestedAt: d.data.requestedAt, keyword: d.data.keyword, status: d.data.status, added: d.data.added, updated: d.data.updated };
        }
      }
      setLastScan(best);
    }).catch(() => setLastScan(null));
    return () => { cancelled = true; };
  }, []);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return rows
      .filter((r) => !isMine(r, company) && (isReadoutType(r) || isReadoutRow(r))
        && `${r.asset} ${r.company} ${r.trial || ''} ${r.generic || ''}`.toLowerCase().includes(q))
      .sort((a, b) => (a.status === 'upcoming' ? 0 : 1) - (b.status === 'upcoming' ? 0 : 1) || (a.date || '9').localeCompare(b.date || '9'))
      .slice(0, 8);
  }, [rows, company, query]);

  const scanAgeMs = lastScan ? Date.now() - Date.parse(lastScan.requestedAt) : Infinity;
  const scanAvailable = !lastScan || scanAgeMs >= WEEK_MS;

  const runGeneralScan = () => {
    if (!scanAvailable) { setMsg('A general scan already ran this week.'); return; }
    if (!permissions?.editor) { setMsg('General scans need editor access.'); return; }
    const left = deepLeft(searchLog);
    if (!left) { setMsg(`You've used your 3 source searches this hour. Next one at ${hhmm(deepNext(searchLog))}.`); return; }
    const today = new Date().toISOString().slice(0, 10);
    const slug = `general-scan-${today}`;
    const text = `SEARCH_ID: ${slug}\nKEYWORD: ${cleanTerm(company)}\nFOCUS: PERSPECTIVE SCAN | company: ${company}`;
    catalystTrackerApi.fireTrigger(text).then(() => {
      onRecordSearch();
      setSent(true);
    }).catch(() => setMsg("Couldn't start the scan just now. Try again shortly."));
  };

  const sendRequest = () => {
    if (!target) return;
    if (!permissions?.editor) { setMsg('Requests need editor access. Ask an editor to run it.'); return; }
    const left = deepLeft(searchLog);
    if (!left) { setMsg(`You've used your 3 source searches this hour. Next one at ${hhmm(deepNext(searchLog))}.`); return; }
    const cleanNote = cleanTerm(note);
    const cleanSource = source.trim().slice(0, 300);
    const srcPart = /^https?:\/\//.test(cleanSource) ? ` | source: ${cleanSource}` : '';
    const focus = `PERSPECTIVE LINK | company: ${company} | catalyst: ${target.id} | asset: ${target.asset} (${target.company}) | relationship claimed: ${relationship}${cleanNote ? ` | note: ${cleanNote}` : ''}${srcPart}`;
    const slug = preqSlug(company, target.id);
    const text = `SEARCH_ID: ${slug}\nKEYWORD: ${cleanTerm(target.asset || '')}\nFOCUS: ${focus}`;
    catalystTrackerApi.fireTrigger(text).then(() => {
      onRecordSearch();
      setSent(true);
    }).catch(() => setMsg("Couldn't queue the check just now. Try again shortly."));
  };

  if (sent) {
    return (
      <TrackerModal title="Perspective" onClose={onClose} footer={<button type="button" className="ib primary" onClick={onClose}>Done</button>}>
        <p className="note">{target ? 'Check requested. The card updates once the link is verified.' : `General scan for ${company} started. Links appear on cards once verified.`}</p>
      </TrackerModal>
    );
  }

  if (target) {
    return (
      <TrackerModal
        title="Perspective"
        onClose={onClose}
        footer={<button type="button" className="ib primary" onClick={sendRequest}>Request check</button>}
      >
        <p className="note" style={{ margin: '0 0 10px' }}>
          Tell us how <b>{target.asset}</b> ({target.company}) relates to <b>{company}</b>. A source search checks the
          link in public filings, press releases and registries; once verified, the card shows how this event affects
          {' '}{company} and the bell alerts you.
        </p>
        <div className="form">
          <label className="full">
            Relationship
            <select value={relationship} onChange={(e) => setRelationship(e.target.value as Relationship)}>
              {REL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label(company)}</option>)}
            </select>
          </label>
          <label className="full">
            Which of your drugs, and anything else we should know (optional)
            <input maxLength={160} placeholder="e.g. competes with our DLL3 ADC in 1L ES-SCLC" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <label className="full">
            Source link (optional)
            <input type="url" placeholder="https://" value={source} onChange={(e) => setSource(e.target.value)} />
          </label>
          {msg && <p className="note full">{msg}</p>}
        </div>
      </TrackerModal>
    );
  }

  return (
    <TrackerModal title="Perspective" onClose={onClose}>
      <p className="note" style={{ margin: '0 0 10px' }}>Find the catalyst you expect to affect <b>{company}</b>.</p>
      <div className="form">
        <label className="full" style={{ position: 'relative' }}>
          Catalyst
          <input autoComplete="off" placeholder="Type a drug, company or trial" value={query} onChange={(e) => setQuery(e.target.value)} />
          {query.trim().length >= 2 && (
            <div className="psug" role="listbox">
              {suggestions.length ? suggestions.map((r) => (
                <button type="button" key={r.id} role="option" className="psi" onClick={() => setTarget(r)}>
                  <b>{r.asset}</b><span>{r.company} · {r.trial || r.event} · {r.window || fmtLong(r.date)}</span>
                </button>
              )) : (
                <div className="psi" style={{ cursor: 'default' }}><span>No tracked catalyst matches. Use the search box to look it up first.</span></div>
              )}
            </div>
          )}
        </label>
      </div>
      <div className="gscan">
        <b>Not sure which catalyst?</b> {scanAvailable
          ? <>Run a general scan for {company}: it checks every tracked catalyst for partnerships, combinations and competing programs not yet linked to {company}. Limited to once a week across all users.</>
          : <>A general scan checks every tracked catalyst for partnerships, combinations and competing programs not yet linked to a company. It runs at most once a week across all users.</>}
        {lastScan && (
          <> {' '}Last general scan: {fmtLong(lastScan.requestedAt.slice(0, 10))} for {lastScan.keyword || ''}
            {lastScan.status === 'done' ? ` (${lastScan.added || 0} added, ${lastScan.updated || 0} linked)` : lastScan.status ? ` (${lastScan.status})` : ''}.</>
        )}
        {!scanAvailable && lastScan && (
          <> Next one available {fmtLong(new Date(Date.parse(lastScan.requestedAt) + WEEK_MS).toISOString().slice(0, 10))}.</>
        )}
        {scanAvailable && (
          <div style={{ marginTop: 6 }}>
            <button type="button" className="ib sm" onClick={runGeneralScan}><TrackerIcon name="flask" />Run general scan</button>
          </div>
        )}
      </div>
      {msg && <p className="note">{msg}</p>}
    </TrackerModal>
  );
};

export default PerspectiveRequestModal;
